import React, { useEffect, useState, useCallback, useRef } from "react";
import api from "../axiosSetup";
import CustomNavbar from "../CustomNavbar";
import cfg from "../../config";

const authHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
});

const PHASE_LABEL = { bus: "Hinfahrt", assembly: "Aufstellungsplatz" };
const RESULT_CFG  = {
  confirmed:         { label: "Bestätigt",      bg: "success" },
  already_confirmed: { label: "Bereits bestätigt", bg: "warning" },
};

// ─── Formatierung ────────────────────────────────────────────────────────────
const fmtDateTime = (iso) => {
  if (!iso) return "–";
  const d = new Date(iso);
  return d.toLocaleString("de-DE", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
};

// ─── Hauptkomponente ──────────────────────────────────────────────────────────
const ZRQRScanLog = () => {
  const [rows, setRows]         = useState([]);
  const [moves, setMoves]       = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [moveFilter, setMoveFilter] = useState("");
  const [limit, setLimit]       = useState(200);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const intervalRef = useRef(null);

  // Umzüge für Filter laden
  useEffect(() => {
    api.get("/api/moves-simple", authHeader())
      .then((r) => setMoves(r.data))
      .catch(() => {});
  }, []);

  const fetchLog = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit });
      if (moveFilter) params.set("moveID", moveFilter);
      const res = await api.get(`/api/qr-scan-log?${params}`, authHeader());
      setRows(res.data);
    } catch (err) {
      setError(err.response?.data?.message || "Fehler beim Laden des Logs");
    } finally {
      setLoading(false);
    }
  }, [moveFilter, limit]);

  // Initial + bei Filteränderung laden
  useEffect(() => { fetchLog(); }, [fetchLog]);

  // Auto-Refresh alle 10 s
  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(fetchLog, 10_000);
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [autoRefresh, fetchLog]);

  // ── Statistiken ────────────────────────────────────────────────────────────
  const total       = rows.length;
  const confirmed   = rows.filter((r) => r.result === "confirmed").length;
  const duplicates  = rows.filter((r) => r.result === "already_confirmed").length;

  return (
    <div>
      <CustomNavbar />
      <div className="container-fluid mt-4 px-3 px-md-4">

        {/* Header */}
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
          <div>
            <h3 className="mb-0">QR-Code Audit-Log</h3>
            <small className="text-muted">Alle Scan-Ereignisse nachvollziehbar dokumentiert</small>
          </div>
          <div className="d-flex gap-2 align-items-center">
            <div className="form-check form-switch mb-0">
              <input
                className="form-check-input"
                type="checkbox"
                id="autoRefresh"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
              <label className="form-check-label small" htmlFor="autoRefresh">
                Live (10 s)
              </label>
            </div>
            <button className="btn btn-outline-secondary btn-sm" onClick={fetchLog} disabled={loading}>
              {loading ? (
                <span className="spinner-border spinner-border-sm" />
              ) : (
                <i className="bi bi-arrow-clockwise" />
              )}{" "}
              Aktualisieren
            </button>
          </div>
        </div>

        {/* Statistik-Karten */}
        <div className="row g-2 mb-3">
          {[
            { label: "Scans gesamt",    value: total,      color: "primary" },
            { label: "Erfolgreich",      value: confirmed,  color: "success" },
            { label: "Doppel-Scans",     value: duplicates, color: "warning" },
          ].map(({ label, value, color }) => (
            <div key={label} className="col-4 col-md-2">
              <div className={`card border-${color} text-center py-2`}>
                <div className={`fs-4 fw-bold text-${color}`}>{value}</div>
                <div className="small text-muted">{label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Filter */}
        <div className="row g-2 mb-3">
          <div className="col-12 col-md-5">
            <select
              className="form-select form-select-sm"
              value={moveFilter}
              onChange={(e) => setMoveFilter(e.target.value)}
            >
              <option value="">Alle {cfg.eventTermPlural}</option>
              {moves.map((m) => (
                <option key={m.ID} value={m.ID}>
                  {m.city} – {m.date}
                </option>
              ))}
            </select>
          </div>
          <div className="col-6 col-md-2">
            <select
              className="form-select form-select-sm"
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
            >
              {[50, 100, 200, 500, 1000].map((n) => (
                <option key={n} value={n}>Max. {n} Einträge</option>
              ))}
            </select>
          </div>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}

        {/* Tabelle Desktop / Cards Mobile */}
        {rows.length === 0 && !loading ? (
          <div className="alert alert-info">Keine Scan-Ereignisse gefunden.</div>
        ) : (
          <>
            {/* ── Tabelle (ab md) ── */}
            <div className="d-none d-md-block">
              <div className="table-responsive">
                <table className="table table-hover table-sm align-middle">
                  <thead className="table-dark">
                    <tr>
                      <th>Zeitstempel</th>
                      <th>{cfg.memberTerm}</th>
                      <th>{cfg.costumeTerm}</th>
                      <th>{cfg.eventTerm}</th>
                      <th>Phase</th>
                      <th>Ergebnis</th>
                      <th>Gescannt von</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const res = RESULT_CFG[r.result] ?? { label: r.result, bg: "secondary" };
                      return (
                        <tr key={r.id}>
                          <td className="text-nowrap small">{fmtDateTime(r.scannedAt)}</td>
                          <td>{r.memberName}</td>
                          <td className="text-nowrap">
                            {r.haesTyp} {r.haesNumber}
                            {r.isKidsHaes ? <span className="badge bg-info ms-1 small">Kind</span> : null}
                          </td>
                          <td className="text-nowrap">{r.moveCity}<br /><small className="text-muted">{r.moveDate}</small></td>
                          <td>{PHASE_LABEL[r.phase] ?? r.phase}</td>
                          <td>
                            <span className={`badge bg-${res.bg} text-${res.bg === "warning" ? "dark" : "white"}`}>
                              {res.label}
                            </span>
                          </td>
                          <td>{r.scannedByName}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ── Cards (bis md) ── */}
            <div className="d-md-none d-flex flex-column gap-2">
              {rows.map((r) => {
                const res = RESULT_CFG[r.result] ?? { label: r.result, bg: "secondary" };
                return (
                  <div key={r.id} className="card shadow-sm">
                    <div className="card-body py-2 px-3">
                      <div className="d-flex justify-content-between align-items-start mb-1">
                        <span className="small text-muted">{fmtDateTime(r.scannedAt)}</span>
                        <span className={`badge bg-${res.bg} text-${res.bg === "warning" ? "dark" : "white"}`}>
                          {res.label}
                        </span>
                      </div>
                      <div className="fw-semibold">{r.memberName}</div>
                      <div className="small">
                        {r.haesTyp} {r.haesNumber}
                        {r.isKidsHaes ? <span className="badge bg-info ms-1">Kind</span> : null}
                        {" · "}{r.moveCity} {r.moveDate}
                      </div>
                      <div className="small text-muted mt-1">
                        Phase: <strong>{PHASE_LABEL[r.phase] ?? r.phase}</strong>
                        {" · "}Gescannt von: <strong>{r.scannedByName}</strong>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ZRQRScanLog;
